import React, { useState } from 'react';
import ReactMarkdown from "react-markdown";
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import ChakraUIRenderer from 'chakra-ui-markdown-renderer';
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw';
import {Link, Button, Spinner, useMediaQuery, Box, Text} from '@chakra-ui/react';
import { url } from '../lib/constants';
import fetchWrapper from '../lib/fetch-wrapper';

function citationLabel(input: string): string {
  const pageRegex = /,\s*Page\s*(\d+)$/i;
  const match = input.match(pageRegex);
  const pageSuffix = match ? ` · p.${match[1]}` : '';
  const title = (match ? input.replace(pageRegex, '') : input).trim();
  return title + pageSuffix;
}

export const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
  const isMobile = useMediaQuery("(max-width: 768px)")[0];
  const [documentData, setDocumentData] = useState('');

  const newTheme = {
    a: (props: any) => {
      const { href, children } = props;

      if (href?.includes("blob.core.windows")) {
        const documentName = children?.toString() || "Download Document";
        const pageMatch = documentName.match(/Page\s*(\d+)/i);
        const pageNumber = pageMatch ? pageMatch[1] : null;
        const label = citationLabel(documentName);

        return (
            <Button
                display="inline-flex"
                alignItems="flex-start"
                size="sm"
                px={3}
                py={2}
                my={1}
                maxW="100%"
                minW={0}
                height="auto"
                whiteSpace="normal"
                textAlign="left"
                variant="brand"
                title={documentName}
                onClick={async () => {
                    setDocumentData(documentName);
                    try {
                        const fw = new fetchWrapper();
                        const response = await fw.getDocument(
                            `${url}/api/document?url=${encodeURIComponent(href)}`
                        );

                        if (response.status === 200) {
                            const blob = response.data;
                            const blobUrl = window.URL.createObjectURL(blob);

                            if (isMobile) {
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = documentName || href.split('/').pop() || 'document.pdf';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                            } else {
                                const openUrl = pageNumber ? `${blobUrl}#page=${pageNumber}` : blobUrl;
                                window.open(openUrl, '_blank');
                            }
                        } else {
                            console.error("Failed to load document");
                        }

                        setDocumentData('');
                    } catch (error) {
                        setDocumentData('');
                        console.error("Error fetching document", error);
                    }
                }}
                isDisabled={documentData === documentName}
                leftIcon={documentData === documentName ? <Spinner size="xs" data-testid="spinner" /> : undefined}
            >
              <Text
                as="span"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
                noOfLines={3}
                lineHeight="1.35"
              >
                {label}
              </Text>
            </Button>
        );
      }

      return <Link href={href} color={'blue.500'} wordBreak="break-all">{children}</Link>;
    }
  };

  return (
      <Box
          minW={0}
          maxW="100%"
          overflowX="hidden"
          wordBreak="break-word"
          overflowWrap="anywhere"
      >
          <ReactMarkdown components={ChakraUIRenderer(newTheme)}
                         children={content}
                         skipHtml remarkPlugins={[remarkGfm]}
                         rehypePlugins={[rehypeRaw]}
                         includeElementIndex={true}
          />
      </Box>
  );
}
